import { useCallback, useEffect, useMemo, useState } from 'react'
import { GROUP_TYPE } from '../../domain/domainCodes'
import { Link } from 'react-router-dom'
import { Plus, ShieldCheck, Trash2, Users } from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import ExcelUploadFields from '../../components/ExcelUploadFields'
import ExportMenu from '../../components/ExportMenu'
import FilterBar from '../../components/FilterBar'
import { InfoNote } from '../../components/Form'
import SegmentedControl from '../../components/SegmentedControl'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import { exportGroupUsers, listGroupUsers } from './groupsService'
import { listGroupOptions, listUsers } from '../users/usersService'
import { strings as S } from './strings'
import './modals.css'

const EXCEL_COLUMNS = [{ name: 'Username', required: true }]

const SOURCE_TABS = [
  { id: 'list', label: S.addUsersModal.tabs.list },
  { id: 'excel', label: S.addUsersModal.tabs.excel },
]

/** A fresh list starts at the top, in the size every other table here starts at. */
const PAGE_ONE = { page: 1, pageSize: 10, sort: undefined }

/**
 * One group panel, replacing `#users_modal` + `#add_users_modal`.
 *
 * The legacy chains them: the members modal *closes itself* to open the picker, and
 * submitting the picker returns you to neither — so adding members destroys the view
 * you were adding them to. Here both are tabs of one drawer over the still-visible
 * list, and adding members drops you back on the members tab with the new rows in it.
 *
 * The `removeActions` rule is unchanged: everything that edits membership only shows
 * when the group is not a department (`row._5 !== "1"`, js.cshtml:395-419).
 */
export default function GroupMembersDrawer({ open, group, onClose, onAddUsers, onRemoveUsers }) {
  const [tab, setTab] = useState('members')

  /**
   * BOTH LISTS ARE SERVER-PAGED, and both were being read as if they were not.
   *
   * `listGroupUsers` and `listUsers` default to `page: 1, pageSize: 10`; this drawer called
   * each with no paging argument at all and handed the ten rows that came back to a table
   * as though they were the whole list. So a group with two hundred members showed ten, and
   * — worse — the picker offered only the first ten people in the directory, with no search
   * and no second page, which left the other users of the tenant unaddable to any group.
   *
   * The state below is what the users page keeps for the same endpoint: the page, the page
   * size and the sort go UP to the server, and `total` comes back to drive the pager.
   */
  const [members, setMembers] = useState([])
  const [memberTotal, setMemberTotal] = useState(0)
  const [memberState, setMemberState] = useState(PAGE_ONE)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState([])
  const [query, setQuery] = useState('')
  /** Bumped to re-read the current page after a write, without changing what page it is. */
  const [reloadNonce, setReloadNonce] = useState(0)

  const [source, setSource] = useState('list')
  const [groupFilter, setGroupFilter] = useState('')
  const [groupOptions, setGroupOptions] = useState([])
  const [candidates, setCandidates] = useState([])
  const [candidateTotal, setCandidateTotal] = useState(0)
  const [candidateState, setCandidateState] = useState(PAGE_ONE)
  const [candidateQuery, setCandidateQuery] = useState('')
  const [candidatesLoading, setCandidatesLoading] = useState(true)
  const [picked, setPicked] = useState([])
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  /** One in-flight action at a time — these all write membership. */
  const [busy, setBusy] = useState(false)

  const editable = group?.typeId !== GROUP_TYPE.DEPARTMENTS

  /** Re-read the page that is open, after a write that changed what is on it. */
  const loadMembers = useCallback(() => setReloadNonce((nonce) => nonce + 1), [])

  /* Opening the drawer on a different group is a different list: back to the top, with
     nothing carried over from the group that was open before. */
  useEffect(() => {
    if (!open || !group) return
    setTab('members')
    setSelected([])
    setPicked([])
    setFile(null)
    setError('')
    setQuery('')
    setCandidateQuery('')
    setGroupFilter('')
    setMemberState(PAGE_ONE)
    setCandidateState(PAGE_ONE)
    setBusy(false)
  }, [open, group])

  /* A NEW SEARCH IS A NEW LIST, so it starts at page 1 — otherwise a search run from
     page 4 asks the server for the fourth page of a result set that may have one.
     The same object comes back when the page is already 1, so this never causes a
     second read of a list that was about to be read anyway. */
  useEffect(() => {
    setMemberState((state) => (state.page === 1 ? state : { ...state, page: 1 }))
  }, [query])

  useEffect(() => {
    setCandidateState((state) => (state.page === 1 ? state : { ...state, page: 1 }))
  }, [candidateQuery, groupFilter])

  useEffect(() => {
    if (!open || !group) return undefined
    let cancelled = false
    setLoading(true)

    listGroupUsers(group.id, { ...memberState, search: query })
      .then((response) => {
        if (cancelled) return
        setMembers(response.rows)
        setMemberTotal(response.total)
        setLoading(false)
      })
      .catch(() => {
        /* The page toasts the reason; here the table just stops claiming to be loading. */
        if (cancelled) return
        setMembers([])
        setMemberTotal(0)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, group, memberState, query, reloadNonce])

  useEffect(() => {
    listGroupOptions().then(setGroupOptions)
  }, [])

  useEffect(() => {
    if (!open || tab !== 'add') return undefined
    let cancelled = false
    setCandidatesLoading(true)

    listUsers({
      type: 'ACTIVE',
      groupId: groupFilter === '' ? null : Number(groupFilter),
      ...candidateState,
      search: candidateQuery,
    })
      .then((response) => {
        if (cancelled) return
        setCandidates(response.rows)
        setCandidateTotal(response.total)
        setCandidatesLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setCandidates([])
        setCandidateTotal(0)
        setCandidatesLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, tab, groupFilter, candidateState, candidateQuery])

  const toggle = (setter) => (userName) =>
    setter((previous) =>
      previous.includes(userName)
        ? previous.filter((item) => item !== userName)
        : [...previous, userName],
    )

  const memberColumns = useMemo(
    () => [
      {
        key: 'userName',
        header: S.usersModal.columns.userName,
        sortable: false,
        render: (row) => (
          <Link className="group-modal__link" to={`/users/${row.id}`}>
            <bdi>{row.userName}</bdi>
          </Link>
        ),
      },
      {
        key: 'name',
        header: S.usersModal.columns.name,
        sortable: false,
        render: (row) => <bdi>{row.name}</bdi>,
      },
      {
        key: 'email',
        header: S.usersModal.columns.email,
        sortable: false,
        render: (row) => (
          <span className="group-modal__mail" dir="ltr">
            {row.email}
          </span>
        ),
      },
      {
        key: 'status',
        header: S.usersModal.columns.status,
        sortable: false,
        value: (row) => row.status.map((badge) => badge.label).join(' '),
        render: (row) => (
          <span className="group-modal__chips">
            {row.status.map((badge) => (
              <StatusBadge key={badge.label} tone={badge.color}>
                {badge.label}
              </StatusBadge>
            ))}
          </span>
        ),
      },
    ],
    [],
  )

  const candidateColumns = useMemo(
    () => [
      {
        key: 'userName',
        header: S.addUsersModal.columns.userName,
        render: (row) => <bdi className="group-modal__link">{row.userName}</bdi>,
      },
      { key: 'name', header: S.addUsersModal.columns.nameAr, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'email',
        header: S.addUsersModal.columns.email,
        render: (row) => (
          <span className="group-modal__mail" dir="ltr">
            {row.email}
          </span>
        ),
      },
      {
        key: 'groupNames',
        header: S.addUsersModal.columns.group,
        /* NOT SORTABLE. The server orders by the column NAMES `listUsers` declares, and
           this is not one of them — so the header offered an arrow that sent nothing and
           changed nothing. */
        sortable: false,
        value: (row) => row.groupNames.join('، '),
        render: (row) => <bdi>{row.groupNames[0] ?? ''}</bdi>,
      },
    ],
    [],
  )

  /**
   * Adding members no longer destroys the members view — it refreshes it.
   *
   * And only once the server has taken the change: a rejected file or a list of names
   * that do not exist leaves the selection where it is, on the tab that made it, so the
   * fix is one edit away rather than a re-pick from scratch.
   */
  const commitAdd = async (payload) => {
    setBusy(true)
    const outcome = await onAddUsers?.(payload)
    setBusy(false)
    if (outcome && !outcome.ok) return

    setPicked([])
    setFile(null)
    setError('')
    setTab('members')
    loadMembers()
  }

  const submitList = () => {
    if (picked.length === 0) return setError(S.addUsersModal.nothingSelected)
    return commitAdd({ users: picked, userGroup: group?.id })
  }

  const submitExcel = () => {
    if (!file) return setError(S.addUsersModal.nothingSelected)
    return commitAdd({ excelFile: file, userGroup: group?.id })
  }

  const submitRemove = async () => {
    setBusy(true)
    const outcome = await onRemoveUsers?.(selected)
    setBusy(false)
    if (outcome && !outcome.ok) return

    setSelected([])
    loadMembers()
  }

  /** `EXCEL` | `PDF` | `DOC` server-side; the menu offers the first two. */
  const runExport = async (format) => {
    setBusy(true)
    const outcome = await exportGroupUsers({ groupId: group?.id, format, search: query })
    setBusy(false)
    if (!outcome.ok) setError(outcome.message)
  }

  /* THE GROUP'S SIZE, not the size of the page of it that is on screen. `members.length`
     is at most one page, so every group with more than ten members was labelled "10". */
  const tabs = editable
    ? [
        { id: 'members', label: S.usersModal.title, count: memberTotal },
        { id: 'add', label: S.usersModal.addUsers },
      ]
    : [{ id: 'members', label: S.usersModal.title, count: memberTotal }]

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      title={group?.name ?? S.usersModal.title}
      icon={Users}
      subtitle={group?.typeName}
      onClose={onClose}
      footer={
        tab === 'members' ? (
          <>
            {editable && (
              <Button
                variant="danger-secondary"
                disabled={selected.length === 0 || busy}
                onClick={submitRemove}
              >
                <Trash2 size={16} strokeWidth={1.75} />
                {/* THE COUNT, because the list is paged now: a member ticked on page 1 is
                    off screen by page 2, and the button is the only thing left saying how
                    many this is about to remove. */}
                {selected.length > 0
                  ? `${S.usersModal.removeUsers} (${selected.length})`
                  : S.usersModal.removeUsers}
              </Button>
            )}
            <ExportMenu
              onExport={runExport}
              labels={{ excel: S.usersModal.exportExcel, pdf: S.usersModal.exportPdf }}
            />
          </>
        ) : (
          <Button disabled={busy} onClick={source === 'list' ? submitList : submitExcel}>
            <Plus size={16} strokeWidth={2} />
            {source === 'list' && picked.length > 0
              ? `${S.addUsersModal.save} (${picked.length})`
              : S.addUsersModal.save}
          </Button>
        )
      }
    >
      <div className="group-drawer">
        <SegmentedControl items={tabs} value={tab} onChange={setTab} ariaLabel={S.usersModal.title} />

        {tab === 'members' && (
          <>
            {editable && (
              <InfoNote icon={ShieldCheck}>
                <bdi>{S.usersModal.hint}</bdi>
              </InfoNote>
            )}

            <FilterBar
              /* THE SERVER SEARCHES NOW, not the ten rows that happened to be loaded.
                 The placeholder says so: it matches the name and the email too. */
              search={{
                value: query,
                onChange: setQuery,
                placeholder: S.usersModal.search,
              }}
            />

            <DataTable
              /* A list inside a sheet is opened to answer one question; it starts at the top. */
              stateKey={false}
              columns={memberColumns}
              rows={members}
              loading={loading}
              query={query}
              getRowId={(row) => row.id}
              onRowClick={editable ? (row) => toggle(setSelected)(row.userName) : undefined}
              isRowSelected={editable ? (row) => selected.includes(row.userName) : undefined}
              /* Server-paged: `rows` is one page and `total` is the whole group, so the
                 pager counts the members rather than what happens to be on screen. */
              server={{
                total: memberTotal,
                page: memberState.page,
                pageSize: memberState.pageSize,
                sort: memberState.sort,
                onChange: ({ page, pageSize, sort }) => setMemberState({ page, pageSize, sort }),
              }}
            />
          </>
        )}

        {tab === 'add' && (
          <>
            <SegmentedControl
              items={SOURCE_TABS}
              value={source}
              onChange={setSource}
              ariaLabel={S.addUsersModal.title}
            />

            {source === 'list' && (
              <>
                <InfoNote icon={ShieldCheck}>
                  <bdi>{S.addUsersModal.hint}</bdi>
                </InfoNote>

                <FilterBar
                  /* THE SEARCH THIS TAB NEVER HAD. Without it the picker offered the first
                     ten active users and no way to reach anyone else, so a directory of any
                     size had most of its people unaddable to a group. */
                  search={{
                    value: candidateQuery,
                    onChange: setCandidateQuery,
                    placeholder: S.addUsersModal.search,
                  }}
                  chips={
                    groupFilter === ''
                      ? []
                      : [
                          {
                            id: 'group',
                            label: S.addUsersModal.groupPlaceholder,
                            value:
                              groupOptions.find(
                                (option) => String(option.value) === String(groupFilter),
                              )?.label ?? '',
                            onRemove: () => setGroupFilter(''),
                          },
                        ]
                  }
                >
                  <Select
                    value={groupFilter}
                    onChange={setGroupFilter}
                    options={groupOptions}
                    placeholder={S.addUsersModal.groupPlaceholder}
                    aria-label={S.addUsersModal.groupLabel}
                  />
                </FilterBar>

                <DataTable
                  /* A list inside a sheet is opened to answer one question; it starts at the top. */
                  stateKey={false}
                  columns={candidateColumns}
                  rows={candidates}
                  loading={candidatesLoading}
                  query={candidateQuery}
                  getRowId={(row) => row.id}
                  onRowClick={(row) => toggle(setPicked)(row.userName)}
                  /* PICKED, NOT VISIBLE. The selection is a set of usernames held here
                     rather than a property of the rows, so choosing someone on page 1,
                     paging on and choosing someone else keeps both. */
                  isRowSelected={(row) => picked.includes(row.userName)}
                  server={{
                    total: candidateTotal,
                    page: candidateState.page,
                    pageSize: candidateState.pageSize,
                    sort: candidateState.sort,
                    onChange: ({ page, pageSize, sort }) =>
                      setCandidateState({ page, pageSize, sort }),
                  }}
                />
              </>
            )}

            {source === 'excel' && (
              <ExcelUploadFields
                id="group-add-users-file"
                accept=".xls,.xlsx,.csv"
                columns={EXCEL_COLUMNS}
                templateHref="/root/ExcelTemplates/Groups_Private_Users.xlsx"
                error={error}
                strings={S.excel}
                onChange={(chosen) => {
                  setFile(chosen)
                  setError('')
                }}
              />
            )}

            {error && source === 'list' && (
              <p className="form-row__error" role="alert">
                <bdi>{error}</bdi>
              </p>
            )}
          </>
        )}
      </div>
    </Drawer>
  )
}
